import { useState } from "react";
import {
  Button,
  Inline,
  PageHeader,
  Stack
} from "@leylion/design-system";
import type { ReportScope } from "../api/reports-types";
import {
  ComplianceAnalyticsView,
  ExecutiveView,
  OperationsAnalyticsView,
  PeopleAnalyticsView,
  ProfitabilityView,
  RestaurantAnalyticsView,
  SalesView,
  StrategyAnalyticsView
} from "./AnalyticsViews";
import {
  FinanceRebuildAction,
  FinanceWorkspace
} from "./FinanceViews";
import { ReportLinkedDownload } from "./ReportExecutionHistory";
import { ReportBuilderView } from "./ReportBuilderView";
import {
  ScopeBar,
  defaultReportScope,
  type ReportsLanguage
} from "./reports-components";
import { TenantCurrencyBoundary } from "../auth/TenantCurrencyBoundary";
import "./reports.css";

type ReportsView =
  | "overview"
  | "sales"
  | "profitability"
  | "operations"
  | "people"
  | "restaurant"
  | "compliance"
  | "strategy"
  | "finance"
  | "builder";

export function ReportsFinanceCenter(props: { language: ReportsLanguage; permissions: string[] }) {
  return <TenantCurrencyBoundary language={props.language}>{currency => <ReportsFinanceWorkspace {...props} initialCurrency={currency} />}</TenantCurrencyBoundary>;
}

function ReportsFinanceWorkspace({
  language,
  permissions,
  initialCurrency
}: {
  language: ReportsLanguage;
  permissions: string[];
  initialCurrency: string;
}) {
  const ar = language === "ar";
  const [view, setView] = useState<ReportsView>("overview");
  const [scopeDraft, setScopeDraft] =
    useState<ReportScope>(() => defaultReportScope(initialCurrency));
  const [scope, setScope] =
    useState<ReportScope>(() => defaultReportScope(initialCurrency));
  const [refreshKey, setRefreshKey] = useState(0);

  const can = (permission: string) =>
    permissions.includes(permission);

  const items: Array<{
    key: ReportsView;
    ar: string;
    en: string;
    visible: boolean;
  }> = [
    {
      key: "overview",
      ar: "لوحة الإدارة",
      en: "Executive",
      visible: can("analytics.read")
    },
    {
      key: "sales",
      ar: "المبيعات",
      en: "Sales",
      visible: can("reports.sales.read")
    },
    {
      key: "profitability",
      ar: "الربحية",
      en: "Profitability",
      visible: can("reports.finance.read")
    },
    {
      key: "operations",
      ar: "المنتجات والمخزون",
      en: "Operations",
      visible:
        can("analytics.read")
        || can("reports.inventory.read")
    },
    {
      key: "people",
      ar: "العملاء والموظفون",
      en: "People",
      visible: can("analytics.read")
    },
    {
      key: "restaurant",
      ar: "المطعم",
      en: "Restaurant",
      visible: can("analytics.read")
    },
    {
      key: "compliance",
      ar: "الضريبة والمدفوعات",
      en: "VAT & Payments",
      visible: can("reports.finance.read")
    },
    {
      key: "strategy",
      ar: "الفروع والتوقعات",
      en: "Branches & Forecast",
      visible:
        can("reports.enterprise")
        || can("analytics.read")
    },
    {
      key: "finance",
      ar: "المحاسبة",
      en: "Finance",
      visible: can("reports.finance.read")
    },
    {
      key: "builder",
      ar: "منشئ التقارير",
      en: "Report Builder",
      visible:
        can("reports.builder")
        || can("reports.schedule")
    }
  ];

  const visibleItems = items.filter((item) => item.visible);
  const effectiveView = visibleItems.some((item) => item.key === view)
    ? view
    : visibleItems[0]?.key ?? "overview";

  const applyScope = () => {
    setScope(scopeDraft);
    setRefreshKey((value) => value + 1);
  };

  return (
    <Stack>
      <PageHeader
        eyebrow={
          ar
            ? "الذكاء التشغيلي والمالي"
            : "Operational & Financial Intelligence"
        }
        title={
          ar
            ? "التقارير، التحليلات والمالية"
            : "Reports, Analytics & Finance"
        }
        description={
          ar
            ? "لوحات تنفيذية وتقارير تشغيل وربحية ومحاسبة مزدوجة وضرائب وتوقعات ومنشئ تقارير."
            : "Executive dashboards, operational analytics, profitability, double-entry finance, VAT, forecasting and report builder."
        }
        actions={
          <Inline>
            {can("finance.manage") ? (
              <FinanceRebuildAction
                language={language}
                scope={scope}
                onChanged={() =>
                  setRefreshKey((value) => value + 1)}
              />
            ) : null}
            <Button
              variant="secondary"
              icon="activity"
              onClick={() =>
                setRefreshKey((value) => value + 1)}
            >
              {ar ? "تحديث" : "Refresh"}
            </Button>
          </Inline>
        }
      />

      <ScopeBar
        language={language}
        scope={scopeDraft}
        onChange={setScopeDraft}
        onApply={applyScope}
        allowEnterprise={can("reports.enterprise")}
      />

      <ReportLinkedDownload language={language} />
      <div className="reports-layout">
        <aside className="reports-navigation">
          {visibleItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={
                effectiveView === item.key ? "is-active" : ""
              }
              data-testid={`reports-nav-${item.key}`}
              onClick={() => setView(item.key)}
            >
              <span>{ar ? item.ar : item.en}</span>
            </button>
          ))}
        </aside>

        <main
          className="reports-content"
          data-testid="reports-finance-center"
        >
          {effectiveView === "overview" ? (
            <ExecutiveView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "sales" ? (
            <SalesView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "profitability" ? (
            <ProfitabilityView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "operations" ? (
            <OperationsAnalyticsView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "people" ? (
            <PeopleAnalyticsView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "restaurant" ? (
            <RestaurantAnalyticsView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "compliance" ? (
            <ComplianceAnalyticsView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "strategy" ? (
            <StrategyAnalyticsView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
            />
          ) : null}
          {effectiveView === "finance" ? (
            <FinanceWorkspace
              language={language}
              scope={scope}
              refreshKey={refreshKey}
              permissions={permissions}
              onChanged={() =>
                setRefreshKey((value) => value + 1)}
            />
          ) : null}
          {effectiveView === "builder" ? (
            <ReportBuilderView
              language={language}
              scope={scope}
              refreshKey={refreshKey}
              onScopeChange={(next) => { setScopeDraft(next); setScope(next); }}
              canBuild={can("reports.builder")}
              canSchedule={can("reports.schedule")}
              canExport={can("reports.export")}
              onChanged={() =>
                setRefreshKey((value) => value + 1)}
            />
          ) : null}
        </main>
      </div>
    </Stack>
  );
}
